/**
 * This page provides an overview of the registered models for either one entity
 * or a combined registry overview across the user's teams and personal registered models.
 */
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import * as Tabs from '@wandb/weave/components/Tabs';
import {useEach, useNodeValue} from '@wandb/weave/react';
import React, {FC, useContext, useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {useViewer} from '../../state/viewer/hooks';
import * as urls from '../../util/urls';
import {RegistryName} from '../../util/urls';
import {SelectEntity} from '../Launch/Queues/SelectEntity';
import {CreateTriggerDrawer} from '../Launch/Triggers/CreateTriggerDrawer/CreateTriggerDrawer';
import {getTeamModelRegistryTriggerDrawerInit} from '../Launch/Triggers/CreateTriggerDrawer/EventStore';
import {AllTeamsMigrationStatusBanner} from '../Registries/ModelRegistryMigration/MigrationStatusBanner';
import {ModelRegistryMigrationContext} from '../Registries/ModelRegistryMigration/ModelRegistryMigrationContextProvider';
import {MigrationStatus} from '../Registries/ModelRegistryMigration/utils';
import {SDKVersionProvider} from '../Registries/Registry/SDKVersionContext';
import {CreateRegisteredCollectionDrawer} from '../Registries/workflows/CreateRegisteredCollectionDrawer/CreateRegisteredCollectionDrawer';
import {InstrumentedLoader} from '../utility/InstrumentedLoader';
import {useTeamTriggers} from './hooks';
import * as S from './ModelRegistryPage.styles';
import {ModelRegistryPageHeader} from './ModelRegistryPageHeader';
import {PanelArtifactCollectionCards} from './PanelArtifactCollectionCards';
import {
  artifactsNodeToCollectionInfo,
  AUTOMATIONS_TAB_NAME,
  CollectionInfo,
  COLLECTIONS_TAB_NAME,
  DEFAULT_PROJECT_NAME,
  useModelRegistryArtifactPortfoliosNode,
} from './utils';
import {ZeroModels} from './ZeroModels';

export const AllTeamsModelRegistryPage = () => {
  // Model Registry Migration Context
  const {
    orgMigrationStatus: MRMigrationStatus, // The all teams page should show the status banners based on the organization's migration status
  } = useContext(ModelRegistryMigrationContext);

  // entityName is undefined in the all-teams view
  const entityName = undefined;

  const [selectedTab, setSelectedTab] = useState(COLLECTIONS_TAB_NAME);

  const artifactType = 'model';
  const [page, setPage] = useState(0);
  const history = useHistory();
  const viewer = useViewer();
  const [showNewPortfolioModal, setShowNewPortfolioModal] = useState(false);
  const [showNewAutomationModal, setShowNewAutomationModal] = useState(false);
  const username = viewer?.username;
  const isPersonalEntity = entityName === username;

  // User changed entity filter selection
  const onSelectEntity = (name: string) => {
    const url = name
      ? urls.objectRegistry('model', undefined, name)
      : urls.objectRegistry('model', undefined);
    history.push(url);
    setPage(0);
  };

  const [searchFilter, setSearchFilter] = useState('');
  // Setting artifact type to model as we're in the model registry
  const portfoliosNode = useModelRegistryArtifactPortfoliosNode(
    artifactType,
    entityName
  );
  const search = window.location.search;
  const {selectionPath} = urls.parseEntityRegistrySearch(search);
  const collectionNodes = useEach(portfoliosNode as any);

  // Get list of collection names so that while creating a new collection, the new name can
  // be compared against the existing set. While there is no backend error upon creating a model that
  // already exists, it may cause confusion from a user perspective.
  const collectionNamesDict = useNodeValue(
    useMemo(() => {
      return artifactsNodeToCollectionInfo(portfoliosNode as any);
    }, [portfoliosNode])
  );
  const collectionNames: CollectionInfo[] =
    collectionNamesDict.result?.artifactCollections ?? [];

  const {triggers, projectTriggersQuery} = useTeamTriggers(entityName);

  // Anon user trying to view their own registry
  // wandb.ai/models or wandb.ai/registry/model
  if (!username && !entityName) {
    return (
      <S.Panel>
        <ModelRegistryPageHeader />
        <ZeroModels
          onCreateModel={() => {
            setShowNewPortfolioModal(true);
          }}
        />
      </S.Panel>
    );
  }
  // Upon completion of the migration, we want to hide all model registry content and
  // only display the team selector with the banner.
  if (MRMigrationStatus === MigrationStatus.COMPLETED) {
    return (
      <S.Panel>
        <S.OptionsPanel>
          {viewer != null && (
            <S.SelectPanel>
              <SelectEntity
                size="large"
                value={entityName ?? ''}
                onSelectEntity={onSelectEntity}
                includeAll
              />
            </S.SelectPanel>
          )}
        </S.OptionsPanel>
        <AllTeamsMigrationStatusBanner
          className="mb-24"
          projectName={DEFAULT_PROJECT_NAME}
        />
      </S.Panel>
    );
  }

  return (
    <>
      <S.Panel>
        <ModelRegistryPageHeader
          onCreateModel={() => {
            setShowNewPortfolioModal(true);
          }}
          isPersonalEntity={isPersonalEntity}
          entityName={entityName}
          onCreateAutomation={() => {
            setShowNewAutomationModal(true);
          }}
        />
        <S.OptionsPanel>
          {/*This page is accessible in the anon state since we don't always route via the homepage. To avoid confusion
          since the team-list will always be empty for anon users, it would be better to hide this dropdown*/}
          {viewer != null && (
            <S.SelectPanel>
              <SelectEntity
                size="large"
                value={entityName ?? ''}
                onSelectEntity={onSelectEntity}
                includeAll
              />
            </S.SelectPanel>
          )}
        </S.OptionsPanel>
        <AllTeamsMigrationStatusBanner
          className="mb-24"
          projectName={DEFAULT_PROJECT_NAME}
        />
        <Tabs.Root
          value={selectedTab}
          onValueChange={value => setSelectedTab(value)}>
          <Tabs.List>
            <Tabs.Trigger value={COLLECTIONS_TAB_NAME}>
              Collections
            </Tabs.Trigger>
            <Tabs.Trigger value={AUTOMATIONS_TAB_NAME}>
              Automations
            </Tabs.Trigger>
          </Tabs.List>
          <div className="pt-16">
            <Tabs.Content value={COLLECTIONS_TAB_NAME}>
              {/* SDKVersionProvider needed for ZeroVersions code snippet for Registries.
              Since this is sharing the same collection component, we need to also include it here. */}
              <SDKVersionProvider>
                <CollectionsTab
                  {...{
                    page,
                    setPage,
                    showNewAutomationModal,
                    setShowNewAutomationModal,
                    registryName: 'model',
                    entityName,
                    showNewPortfolioModal,
                    setShowNewPortfolioModal,
                    artifactType,
                    collectionNames,
                    collectionNamesLoading: collectionNamesDict.loading,
                    collectionNodes,
                    portfoliosNode,
                    searchFilter,
                    setSearchFilter,
                    selectionPath,
                  }}
                />
              </SDKVersionProvider>
            </Tabs.Content>
            <Tabs.Content value={AUTOMATIONS_TAB_NAME}>
              <>
                Please select a team in the dropdown above to view its
                automations.
              </>
            </Tabs.Content>
          </div>
        </Tabs.Root>
      </S.Panel>
      <CreateRegisteredCollectionDrawer
        open={showNewPortfolioModal}
        closeDrawer={() => setShowNewPortfolioModal(false)}
        artifactTypeName={artifactType}
        registryEntityName={entityName}
        defaultProjectName={DEFAULT_PROJECT_NAME}
        existingCollections={collectionNames}
      />
      <CreateTriggerDrawer
        open={showNewAutomationModal}
        closeDrawer={() => {
          setShowNewAutomationModal(false);
        }}
        refetchProjectTriggers={projectTriggersQuery.refetch}
        createTriggerDrawerInit={{
          ...getTeamModelRegistryTriggerDrawerInit(entityName),
          isUpdating: false,
          trigger: undefined,
        }}
      />
    </>
  );
};

interface Props {
  entityName: string | undefined;
  registryName?: string;
  page: number;
  setPage: (page: number) => void;
  showNewAutomationModal: boolean;
  setShowNewAutomationModal: (show: boolean) => void;
  showNewPortfolioModal: boolean;
  setShowNewPortfolioModal: (show: boolean) => void;
  artifactType: string;
  collectionNames: CollectionInfo[];
  collectionNamesLoading: boolean;
  collectionNodes: any;
  portfoliosNode: any;
  searchFilter: string;
  setSearchFilter: (value: string) => void;
  selectionPath: urls.SelectionPathType | undefined;
}

const CollectionsTab: FC<Props> = ({
  entityName,
  registryName,
  page,
  setPage,
  setShowNewPortfolioModal,
  collectionNamesLoading,
  collectionNodes,
  portfoliosNode,
  searchFilter,
  setSearchFilter,
  selectionPath,
}) => {
  if (collectionNodes.loading || collectionNamesLoading) {
    return <InstrumentedLoader name="collections-tab-loader" />;
  }
  return (
    <>
      <S.StyledTextField
        size="large"
        placeholder="Search model name and tags"
        value={searchFilter}
        autoFocus={true}
        onChange={(value: string) => setSearchFilter(value)}
        icon="search"
      />
      <div className="mt-16">
        {collectionNodes.result.length === 0 ? (
          <ZeroModels
            onCreateModel={() => {
              setShowNewPortfolioModal(true);
            }}
          />
        ) : (
          <PanelArtifactCollectionCards
            {...Panel2.dummyProps}
            input={portfoliosNode as any}
            searchFilter={searchFilter}
            selectionPath={selectionPath}
            registryName={registryName as RegistryName}
            showEntity={!entityName}
            page={page}
            setPage={setPage}
          />
        )}
      </div>
    </>
  );
};
